import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  TrendingUp, 
  DollarSign, 
  ShoppingCart, 
  Target,
  Users,
  Newspaper,
  Trophy
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { NewsChallengesWidget } from '../components/dashboard/NewsChallengesWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Dashboard = () => {
  const { user, isAdmin, isRH } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Redirect RH to their dedicated dashboard
    if (isRH) {
      navigate('/rh/dashboard', { replace: true });
      return;
    }
    fetchDashboard();
  }, [isAdmin, isRH, navigate]);

  const fetchDashboard = async () => {
    try {
      const endpoint = isAdmin ? '/dashboard/admin' : '/dashboard/commercial';
      const response = await fetch(`${API}${endpoint}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const dashboardData = await response.json();
        setData(dashboardData);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-64 bg-slate-200 rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-slate-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (isAdmin) {
    return <AdminDashboard data={data} />;
  }

  return <CommercialDashboard data={data} />;
};

const CommercialDashboard = ({ data }) => {
  // Calculate rate based on CA (new thresholds: 3500, 6500)
  const monthlyRevenue = data?.monthly_revenue || 0;
  const rate = data?.commission_rate || (monthlyRevenue > 6500 ? 27 : monthlyRevenue > 3500 ? 25 : 22);
  
  const stats = [
    {
      title: 'CA du mois',
      value: `${monthlyRevenue.toLocaleString('fr-FR')} €`,
      icon: TrendingUp,
      color: 'bg-indigo-500',
    },
    {
      title: 'Taux actuel',
      value: `${rate}%`,
      icon: Target,
      color: 'bg-purple-500',
    },
    {
      title: 'Commission estimée',
      value: `${(data?.estimated_commission || 0).toLocaleString('fr-FR', {minimumFractionDigits: 2})} €`,
      icon: DollarSign,
      color: 'bg-emerald-500',
    },
    {
      title: 'Ventes ce mois',
      value: data?.monthly_sales_count || 0,
      icon: ShoppingCart,
      color: 'bg-orange-500',
    },
  ];

  const statusLabels = {
    devis_signe: 'Devis signés',
    facture_envoyee: 'Factures envoyées',
    paiement_recu: 'Paiements reçus',
    securisation: 'En sécurisation',
    commission_eligible: 'Commissions éligibles',
    paye: 'Payés',
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-[Manrope]" data-testid="dashboard-title">
            Tableau de bord
          </h1>
          <p className="text-slate-500 mt-1">Vue d'ensemble de votre activité</p>
        </div>
        <Link to="/declare-sale">
          <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="declare-sale-btn">
            Déclarer une vente
          </Button>
        </Link>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <Card 
            key={stat.title} 
            className="stat-card border-0 shadow-sm animate-fade-in"
            style={{ animationDelay: `${index * 0.05}s` }}
            data-testid={`stat-${stat.title.toLowerCase().replace(/ /g, '-')}`}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className={`p-3 rounded-xl ${stat.color}`}>
                  <stat.icon className="w-5 h-5 text-white" strokeWidth={1.5} />
                </div>
              </div>
              <div className="mt-4">
                <p className="text-sm font-medium text-slate-500">{stat.title}</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
                  {stat.value}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Notifications only */}
      <Card className="border-0 shadow-sm" data-testid="notifications-card">
        <CardHeader>
          <CardTitle className="font-[Manrope]">Notifications récentes</CardTitle>
          <CardDescription>Restez informé de vos activités</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {(data?.notifications || []).slice(0, 5).map((notif) => (
              <div 
                key={notif.notification_id} 
                className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-900"
              >
                <div className="w-2 h-2 mt-2 rounded-full bg-indigo-500" />
                <div>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">
                    {notif.title}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">{notif.message}</p>
                </div>
              </div>
            ))}
            {(data?.notifications || []).length === 0 && (
              <p className="text-sm text-slate-500 text-center py-4">
                Aucune notification
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const AdminDashboard = ({ data }) => {
  const stats = [
    {
      title: 'CA Total',
      value: `${(data?.total_revenue || 0).toLocaleString('fr-FR')} €`,
      icon: TrendingUp,
      color: 'bg-indigo-500',
    },
    {
      title: 'CA Mensuel',
      value: `${(data?.monthly_revenue || 0).toLocaleString('fr-FR')} €`,
      icon: DollarSign,
      color: 'bg-emerald-500',
    },
    {
      title: 'Ventes totales',
      value: data?.total_sales || 0,
      icon: ShoppingCart,
      color: 'bg-violet-500',
    },
    {
      title: 'Commerciaux',
      value: data?.users_count || 0,
      icon: Users,
      color: 'bg-amber-500',
    },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-[Manrope]" data-testid="admin-dashboard-title">
          Administration
        </h1>
        <p className="text-slate-500 mt-1">Vue globale de l'activité commerciale</p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <Card 
            key={stat.title} 
            className="stat-card border-0 shadow-sm animate-fade-in"
            style={{ animationDelay: `${index * 0.05}s` }}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className={`p-3 rounded-xl ${stat.color}`}>
                  <stat.icon className="w-5 h-5 text-white" strokeWidth={1.5} />
                </div>
              </div>
              <div className="mt-4">
                <p className="text-sm font-medium text-slate-500">{stat.title}</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
                  {stat.value}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Commercial ranking */}
      <Card className="border-0 shadow-sm" data-testid="ranking-card">
        <CardHeader>
          <CardTitle className="font-[Manrope]">Classement commerciaux</CardTitle>
          <CardDescription>Top 10 par chiffre d'affaires</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {(data?.commercial_ranking || []).map((commercial, index) => (
              <div 
                key={commercial.user_id} 
                className="flex items-center gap-4 p-3 rounded-lg bg-slate-50 dark:bg-slate-900"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  index === 0 ? 'position-1' : index === 1 ? 'position-2' : index === 2 ? 'position-3' : 'bg-slate-200 text-slate-600'
                }`}>
                  {index + 1}
                </div>
                <Avatar className="w-10 h-10">
                  <AvatarImage src={commercial.picture} />
                  <AvatarFallback className="bg-indigo-100 text-indigo-600">
                    {commercial.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="font-medium text-slate-900 dark:text-white">{commercial.name}</p>
                  <p className="text-xs text-slate-500">{commercial.sales_count} ventes</p>
                </div>
                <p className="font-bold text-slate-900 dark:text-white">
                  {commercial.revenue.toLocaleString('fr-FR')} €
                </p>
              </div>
            ))}
            {(data?.commercial_ranking || []).length === 0 && (
              <p className="text-sm text-slate-500 text-center py-4">
                Aucun commercial pour le moment
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Actualités et Challenges */}
      <NewsChallengesWidget />
    </div>
  );
};
